import type { ReactNode } from 'react';
import { ChevronRightIcon } from '@utils/icons';
import styles from './InventoryLevels.module.css';

interface ISpanOrButtonProps {
  className: string | undefined;
  /** `data-tone` on the element — a block line's urgency colour. Omitted for
   *  inline phrases, which inherit their sentence's tone. */
  tone?: string | undefined;
  onClick?: () => void;
  /** Overrides the accessible name when the visible text does not say where it
   *  goes — "Order now — draft #DEL-300 not sent yet" reads as an instruction,
   *  not a destination. */
  label?: string | undefined;
  children: ReactNode;
}

/**
 * The shared "span when inert, button when it does something" primitive behind
 * both `LineOrButton` and `InlineJumpText`. A control that looks interactive but
 * isn't is worse than no control, so the ELEMENT changes — not just a cursor.
 */
function SpanOrButton({ className, tone, onClick, label, children }: ISpanOrButtonProps) {
  if (!onClick) {
    return (
      <span className={className} data-tone={tone}>
        {children}
      </span>
    );
  }
  return (
    <button type="button" className={className} data-tone={tone} aria-label={label} onClick={onClick}>
      {children}
    </button>
  );
}

interface ILineOrButtonProps extends Omit<ISpanOrButtonProps, 'tone'> {
  tone: string;
}

/**
 * A whole alert LINE, clickable when it has somewhere to go. The whole line is
 * the target — a chevron alone is a small tap area saying "the sentence beside
 * me is not the button", which is not how anyone reads a sentence ending in an
 * arrow. Callers place their own `<JumpChevron/>` in `children` when they want
 * one (the line may end in other glyphs instead).
 */
export function LineOrButton({ className, tone, onClick, label, children }: ILineOrButtonProps) {
  return (
    <SpanOrButton className={className} tone={tone} onClick={onClick} label={label}>
      {children}
    </SpanOrButton>
  );
}

type IInlineJumpTextProps = Omit<ISpanOrButtonProps, 'tone'>;

/**
 * A phrase INSIDE a sentence that jumps to a delivery when it has one to jump
 * to. Unlike `LineOrButton` (a whole standalone line), this sits mid-line next
 * to other controls — the ran-out row's "new delivery arrived" and "Order
 * arrived?" notes, which share their line with the Sync/Dismiss buttons — so
 * only the phrase itself is the target, and it auto-appends the trailing chevron
 * (only when it is a button, so a line that gains a target never shifts).
 */
export function InlineJumpText({ className, onClick, label, children }: IInlineJumpTextProps) {
  return (
    <SpanOrButton className={className} onClick={onClick} label={label}>
      {children}
      {onClick ? <JumpChevron /> : null}
    </SpanOrButton>
  );
}

/** Trailing "…and this goes somewhere" mark. Decoration inside the line's own
 *  button — never a control of its own, which would put a second target inside
 *  the first. */
export function JumpChevron() {
  return (
    <span className={styles.jumpMark} aria-hidden="true">
      <ChevronRightIcon size={13} />
    </span>
  );
}

/** Inline 12px info glyph — the untracked row's line is a statement about
 *  missing data, not about a delivery, so it does not take the truck. */
export function InfoGlyph() {
  return (
    <svg
      width={12}
      height={12}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5" />
      <path d="M12 16h.01" />
    </svg>
  );
}

/** Inline 12px truck glyph for the reorder/gap hint line (spec §3). */
export function TruckGlyph({ size = 12 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M10 17h4V5H2v12h3" />
      <path d="M20 17h2v-3.34a4 4 0 0 0-1.17-2.83L19 9h-5v8h1" />
      <circle cx={7.5} cy={17.5} r={2.5} />
      <circle cx={17.5} cy={17.5} r={2.5} />
    </svg>
  );
}
